/**
 * Compliance Health Score
 *
 * Circular progress indicator showing compliance status.
 * 100 baseline, deductions per urgency level. Frontend-only — no backend needed.
 */

import { useMemo } from 'react';

import { getDeadlinesFromServices } from '../../services/calendarService';

import type { ServiceSummary } from '../../services/servicesService';

interface ComplianceHealthScoreProps {
  services: ServiceSummary[];
}

export function ComplianceHealthScore({ services }: ComplianceHealthScoreProps) {
  const { score, label, color } = useMemo(() => {
    const deadlines = getDeadlinesFromServices(services);
    let s = 100;

    for (const d of deadlines) {
      switch (d.urgency) {
        case 'OVERDUE': s -= 20; break;
        case 'CRITICAL': s -= 10; break;
        case 'URGENT': s -= 5; break;
      }
    }

    s = Math.max(0, s);

    const l = s > 80 ? 'All clear' : s >= 50 ? 'Action needed' : 'Critical — deadlines overdue';
    const c = s > 80
      ? 'hsl(var(--color-success-500))'
      : s >= 50
        ? 'hsl(var(--color-warning-500))'
        : 'hsl(var(--color-error-500))';

    return { score: s, label: l, color: c };
  }, [services]);

  // SVG circular progress
  const radius = 36;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;

  if (services.length === 0) return null;

  return (
    <div
      className="rounded-lg border bg-surface p-5"
      style={{ borderColor: 'hsl(var(--color-neutral-200))' }}
    >
      <h3
        className="text-sm font-medium"
        style={{ color: 'hsl(var(--color-neutral-500))' }}
      >
        Compliance Health
      </h3>
      <div className="mt-3 flex items-center gap-4">
        <div className="relative h-20 w-20 shrink-0">
          <svg className="h-20 w-20 -rotate-90" viewBox="0 0 80 80">
            <circle
              cx="40"
              cy="40"
              r={radius}
              fill="none"
              stroke="hsl(var(--color-neutral-100))"
              strokeWidth="6"
            />
            <circle
              cx="40"
              cy="40"
              r={radius}
              fill="none"
              stroke={color}
              strokeWidth="6"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              style={{ transition: 'stroke-dashoffset 0.5s ease-out' }}
            />
          </svg>
          <span
            className="absolute inset-0 flex items-center justify-center text-lg font-bold"
            style={{ color }}
          >
            {score}
          </span>
        </div>
        <p className="text-sm" style={{ color: 'hsl(var(--color-neutral-700))' }}>
          {label}
        </p>
      </div>
    </div>
  );
}
